import { throttle, isDefined } from './utilities';

/**
 * Makes the checkout's sidebar sticky
 */
export default class StickySidebar {
	/**
	 * Defines the sidebar options, default sticky status, invokes the
	 * required methods and creates an instance of ReviewsVisibility to
	 * set the visibility of reviews dynamically, based on the available
	 * space
	 *
	 * @return {undefined}
	 */
	constructor() {
		// Set some settings
		this.options = {
			// The space we're going to leave above the sidebar, in px
			gap: 15,
		};

		// Set property to save the current stickiness
		this.isCurrentlySticky = false;

		// Change the gap if the admin bar is visible
		this.updateGapBasedOnAdminBar();

		// Listen scrolling on the page
		this.listenScroll();

		// Update when tax is calculated
		this.updateOnTaxCalculation();

		// Evaluate the visibility of the reviews
		new ReviewsVisibility( this.options );
	}

	/**
	 * Listens to the user scrolling and invokes a callback to determine
	 * if the sidebar should be sticky
	 *
	 * @return {undefined}
	 */
	listenScroll() {
		// Listen scroll, but throttle the callback
		// eslint-disable-next-line @wordpress/no-global-event-listener
		window.addEventListener(
			'scroll',
			throttle( () => {
				// Invoke custom callback
				this.onScroll();
			}, 20 )
		);
	}

	/**
	 * Callback invoked every time the user scrolls. Determines if the
	 * sidebar should be sticky at a given scroll position
	 *
	 * @return {undefined}
	 */
	onScroll() {
		// Check if we reached the sidebar position
		if ( this.scrollReachedSidebar && ! this.isCurrentlySticky ) {
			// Change the stickiness
			this.changeSidebarStickiness( true );

			// Change the saved stickiness
			this.isCurrentlySticky = true;
		}

		if ( ! this.scrollReachedSidebar && this.isCurrentlySticky ) {
			// Change the stickiness
			this.changeSidebarStickiness( false );

			// Change the saved stickiness
			this.isCurrentlySticky = false;
		}
	}

	/**
	 * Does the required DOM modifications to the sidebar content node
	 * to change the sticky status of the sidebar
	 *
	 * @param {Boolean} sticky True is the sidebar should be sticky
	 * @return {undefined}
	 */
	changeSidebarStickiness( sticky = false ) {
		// Check if we have to make it sticky
		if ( sticky ) {
			// Change position
			this.$sidebarContent.style.position = 'fixed';

			// Change position from top
			this.$sidebarContent.style.top = `${ this.options.gap }px`;
		} else {
			// Change position
			this.$sidebarContent.style.position = 'relative';

			// Change position from top
			this.$sidebarContent.style.top = 0;
		}
	}

	/**
	 * Calculates the gap based on the visibility of the admin bar. Then,
	 * saves the value on the options.gap property
	 *
	 * @return {undefined}
	 */
	updateGapBasedOnAdminBar() {
		// Check if it has the admin bar
		if ( ! ( this.$adminBar == null ) ) {
			// Add the admin bar height as gap
			this.options.gap += this.$adminBar.offsetHeight;
		}
	}

	/**
	 * Updates the position of the sidebar when the tax is calculated.
	 * We have to do this because EDD re-renders the sidebar completely
	 * when the tax is calculated.
	 *
	 * @return {undefined}
	 */
	updateOnTaxCalculation() {
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_taxes_recalculated', () => {
			// The element will move to the top
			this.isCurrentlySticky = false;

			// Update position
			this.onScroll();
		} );
	}

	/**
	 * Checks if the sidebar is currently sticky
	 *
	 * @return {Boolean} True is it's currently sticky
	 */
	get isCurrentlySticky() {
		return this._isCurrentlySticky;
	}

	/**
	 * Saves the currently sticky status of the sidebar, and triggers
	 * a custom event to the document.body
	 *
	 * @param {Boolean} isSticky True if the sidebar is sticky
	 * @return {undefined}
	 */
	set isCurrentlySticky( isSticky ) {
		// Update value
		this._isCurrentlySticky = isSticky;

		// Trigger a custom event
		const event = new Event( 'uo-edd-checkout/sticky-sidebar/sticky-changed', {
			isSticky: isSticky,
		} );

		document.body.dispatchEvent( event );
	}

	/**
	 * Determines if the current scroll position reached the sidebar position
	 *
	 * @return {Boolean} True if the scroll reached the sidebar position
	 */
	get scrollReachedSidebar() {
		return this.currentScrollPosition > this.sidebarPosition - this.options.gap;
	}

	/**
	 * Returns the current scroll position
	 *
	 * @return {Integer} The scroll position
	 */
	get currentScrollPosition() {
		return document.documentElement.scrollTop;
	}

	/**
	 * Returns the current position of the sidebar from the top, relative
	 * to the document
	 *
	 * @return {Integer} The position of the sidebar from the top
	 */
	get sidebarPosition() {
		return jQuery( this.$sidebar ).offset().top;
	}

	/**
	 * Returns the node of the admin bar
	 *
	 * @return {Node} The admin bar
	 */
	get $adminBar() {
		return document.getElementById( 'wpadminbar' );
	}

	/**
	 * Returns the node of the sidebar
	 *
	 * @return {Node} The sidebar
	 */
	get $sidebar() {
		return document.getElementById( 'edd_checkout_cart_form' );
	}

	/**
	 * Returns the node of the sidebar content
	 *
	 * @return {Node} The sidebar content
	 */
	get $sidebarContent() {
		return document.getElementById( 'edd_checkout_cart_wrap' );
	}
}

/**
 * Determines if the reviews should be visible
 *
 * This class will create a method to look at the height of the
 * window and sidebar to determine if the sidebar will overflow
 * the window when it's fixed.
 *
 * To do so, it will hide all reviews and will start showing
 * reviews one by one, while evaluating the dimensions of the
 * sidebar content and window.
 *
 * The method will be called on page load, and every time the
 * window is resized, with a delay of 500ms.
 */
class ReviewsVisibility {
	/**
	 * Saves required data, checks if the site has reviews and invokes
	 * the required methods to set the visibility of the reviews dynamically
	 *
	 * @param {Object} sidebarOptions Sidebar options, such as the sidebar gap
	 * @return {undefined}
	 */
	constructor( sidebarOptions = {} ) {
		// Save the sidebar options
		this.sidebarOptions = sidebarOptions;

		// Check if the reviews container is defined
		if ( this.hasReviews ) {
			// Evaluate the visibility of the reviews on load
			this.evaluateVisibility();

			// Listen when the window is resized
			this.listenWindowResize();

			// Listen when the gateway changes
			this.listenPaymentChange();

			// Listen when the tax is recalculated
			this.listenTaxesCalculated();
		}
	}

	/**
	 * Listens changes to the window size and evaluates the visibility
	 * of the reviews again
	 *
	 * @return {undefined}
	 */
	listenWindowResize() {
		// Listen resize, but throttle the callback
		// eslint-disable-next-line @wordpress/no-global-event-listener
		window.addEventListener(
			'resize',
			throttle( () => {
				// Evaluate the visibility of the reviews
				this.evaluateVisibility();
			}, 500 )
		);
	}

	/**
	 * Listens changes to the payment method and evaluates the visibility
	 * of the reviews again
	 *
	 * @return {undefined}
	 */
	listenPaymentChange() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Evaluate the visibility of the reviews
			this.evaluateVisibility();
		} );
	}

	/**
	 * Listens to the tax calculation and evaluates the visibility
	 * of the reviews again. We have to do this because EDD removes
	 * all the sidebar elements when it happens
	 *
	 * @return {undefined}
	 */
	listenTaxesCalculated() {
		// Listen when the tax is recalculated
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_taxes_recalculated', () => {
			// Evaluate the visibility of the reviews
			this.evaluateVisibility();
		} );
	}

	/**
	 * Calculates the available space and shows as many reviews
	 * as possible
	 *
	 * @return {undefined}
	 */
	evaluateVisibility() {
		// Hide all the reviews
		this.hideAllReviews();

		// Check if the content of the sidebar overflows the window
		if ( ! this.sidebarOverflowWindow ) {
			// Iterate reviews, and show them one by one
			// We're using .some here because we want to stop the iteration
			// when all the available space on the sidebar is used
			[ ...this.$reviews ].some( ( $review ) => {
				// Show the review
				$review.style.display = 'block';

				// Evaluate if it content overflows the window now
				if ( this.sidebarOverflowWindow ) {
					// Hide the last displayed element
					$review.style.display = 'none';

					// Stop the execution
					return true;
				} else {
					return false;
				}
			} );
		}
	}

	/**
	 * Hides all reviews on the page
	 *
	 * @return {undefined}
	 */
	hideAllReviews() {
		// Iterate reviews
		[ ...this.$reviews ].forEach( ( $review ) => {
			// Hide it using display none
			$review.style.display = 'none';
		} );
	}

	/**
	 * Determines if the sidebar is overflowing window
	 *
	 * @return {Boolean} True if the sidebar is overflowing window
	 */
	get sidebarOverflowWindow() {
		return this.sidebarContentHeight + this.sidebarOptions.gap > this.windowHeight;
	}

	/**
	 * Returns the height of the window
	 *
	 * @return {Integer} The height of the window
	 */
	get windowHeight() {
		return window.innerHeight;
	}

	/**
	 * Returns the height of the sidebar content
	 *
	 * @return {Integer} The height of the sidebar content
	 */
	get sidebarContentHeight() {
		return this.$sidebarContent.offsetHeight;
	}

	/**
	 * Returns the node of the checkout wrapper
	 *
	 * @return {Node} The checkout wrapper
	 */
	get $sidebarContent() {
		return document.getElementById( 'edd_checkout_cart_wrap' );
	}

	/**
	 * Returns the node of the review wrapper
	 *
	 * @return {Node} The review wrapper
	 */
	get $reviewsContainer() {
		// Reviews on the sidebar (not the mobile copy)
		return document.querySelector( '#edd_checkout_cart_wrap .sidebar-reviews' );
	}

	/**
	 * Returns the node of the reviews (multiple nodes)
	 *
	 * @return {NodeList} The reviews
	 */
	get $reviews() {
		// Reviews on the sidebar, not the hidden copy
		return document.querySelectorAll( '#edd_checkout_cart_wrap .sidebar-reviews-item' );
	}

	/**
	 * Checks if the page has the reviews container
	 *
	 * @return {Boolean} True if it has the reviews container
	 */
	get hasReviews() {
		return isDefined( this.$reviewsContainer );
	}
}
